import {
  TDocumentGenerationStepOutputData,
  WorkflowRuntimeStepStatus,
  WorkflowStepDocumentGenerationOutcome,
} from '@commenda/types';
import { useMemo } from 'react';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { ServiceRequestTaskLabel } from '../../labels';
import { ServiceRequestTaskIconConfig } from '../../taskIconConfig';
import { WorkflowGeneratedDocumentButton } from './WorkflowGeneratedDocumentButton';
import { WorkflowNoDocumentNotice } from './WorkflowNoDocumentNotice';

const RUNTIME_STATUS_LABEL: Record<WorkflowRuntimeStepStatus, string> = {
  [WorkflowRuntimeStepStatus.NOT_STARTED]: 'Not Started',
  [WorkflowRuntimeStepStatus.IN_PROGRESS]: 'In Progress',
  [WorkflowRuntimeStepStatus.COMPLETED]: 'Completed',
  [WorkflowRuntimeStepStatus.FAILED]: 'Failed',
  [WorkflowRuntimeStepStatus.DISABLED]: 'Disabled',
};

export const WorkflowSystemTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, workflowExecution } = context;

  const stepExecution = useMemo(() => {
    if (
      !workflowExecution?.runtime?.stepExecutions ||
      !task.workflowDefinitionStepId
    ) {
      return null;
    }

    return (
      Object.values(workflowExecution.runtime.stepExecutions).find(
        (se) => se.stepDefinitionId === task.workflowDefinitionStepId
      ) ?? null
    );
  }, [workflowExecution, task.workflowDefinitionStepId]);

  const runtimeStepStatus = stepExecution?.status ?? null;

  const statusLabel = runtimeStepStatus
    ? RUNTIME_STATUS_LABEL[runtimeStepStatus]
    : 'Not Started';

  // Stamped by the backend only on agent steps that did document work; other steps render unchanged.
  const documentGeneration: TDocumentGenerationStepOutputData | undefined =
    stepExecution?.outputData?.documentGeneration;

  const generatedDocumentButton =
    documentGeneration?.outcome ===
      WorkflowStepDocumentGenerationOutcome.GENERATED &&
    workflowExecution?.id &&
    task.workflowDefinitionStepId ? (
      <WorkflowGeneratedDocumentButton
        workflowExecutionId={workflowExecution.id}
        stepId={task.workflowDefinitionStepId}
      />
    ) : null;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: () => ({
      actions: generatedDocumentButton,
      label: (
        <ServiceRequestTaskLabel
          label={`${task.title} — ${statusLabel}`}
          status={task.status}
          timestamp={task.status === 'COMPLETED' ? task.updatedAt : undefined}
        >
          {documentGeneration?.outcome ===
            WorkflowStepDocumentGenerationOutcome.NONE && (
            <WorkflowNoDocumentNotice summary={documentGeneration.summary} />
          )}
        </ServiceRequestTaskLabel>
      ),
    }),
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{ role, status: task.status }}
      config={config}
      context={context}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
